import { AnyAction } from 'redux';
import { Insurance } from 'store/findProvider/types';
import { Thunk, RootDispatch, RootState } from 'store/types';
import Config from 'react-native-config';
import { cleanPhone } from 'modules/utils/PhoneUtils';
import {
  DispatcherCategoryService,
  DispatcherAuthPrefix,
  DispatcherResource,
  DispatcherEndpoint,
  AdapterSource
} from 'modules/types/common';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { termsConditions } from 'lib/constants/termsAndConditions';
import { DFDHeaders, DFDRequestHeaders } from 'serverTypes/authentication';
import {
  Consumer,
  SendPinProps,
  ValidatePinProps,
  RequestedProxy,
  UpdateEmailProps
} from './types';
import {
  profileConsumerSelector,
  profileRequestedProxies,
  profileRequestStateSelector,
  profileInsuranceRequestStateSelector,
  getGrantPersonIdByConsumerId
} from './selectors';

// for first person using the app including seperate actions for handling insurance data
export const RESET_PROFILE = 'PROFILE.RESET_PROFILE';
export const GET_PROFILE = 'PROFILE.GET_PROFILE';
export const GET_PROFILE_SUCCESS = 'PROFILE.GET_PROFILE_SUCCESS';
export const GET_PROFILE_FAIL = 'PROFILE.GET_PROFILE_FAIL';

export const GET_PROFILE_FOR_INSURANCE = 'PROFILE.GET_PROFILE_FOR_INSURANCE';
export const GET_PROFILE_FOR_INSURANCE_SUCCESS = 'PROFILE.GET_PROFILE_FOR_INSURANCE_SUCCESS';
export const GET_PROFILE_FOR_INSURANCE_FAIL = 'PROFILE.GET_PROFILE_FOR_INSURANCE_FAIL';

// for proxies/future consumer requests including seperate actions for handling insurance data with consumerId
export const GET_PROFILE_FOR_CONSUMER = 'PROFILE.GET_PROFILE_FOR_CONSUMER';
export const GET_PROFILE_FOR_CONSUMER_SUCCESS = 'PROFILE.GET_PROFILE_FOR_CONSUMER_SUCCESS';
export const GET_PROFILE_FOR_CONSUMER_FAIL = 'PROFILE.GET_PROFILE_FOR_CONSUMER_FAIL';

export const GET_PROFILE_FOR_CONSUMER_INSURANCE = 'PROFILE.GET_PROFILE_FOR_CONSUMER_INSURANCE';
export const GET_PROFILE_FOR_CONSUMER_INSURANCE_SUCCESS =
  'PROFILE.GET_PROFILE_FOR_CONSUMER_INSURANCE_SUCCESS';
export const GET_PROFILE_FOR_CONSUMER_INSURANCE_FAIL =
  'PROFILE.GET_PROFILE_FOR_CONSUMER_INSURANCE_FAIL';

export const ACCEPT_T_AND_C = 'PROFILE.ACCEPT_T_AND_C';

// for proxies request
export const ADD_REQUESTED_PROXIES = 'PROFILE.ADD_REQUESTED_PROXIES';
export const CLEAR_REQUESTED_PROXIES = 'PROFILE.CLEAR_REQUESTED_PROXIES';
export const DELETE_REQUESTED_PROXY = 'PROFILE.DELETE_REQUESTED_PROXY';
export const SET_PROXY_TO_UPDATE = 'PROFILE.SET_PROXY_TO_UPDATE';
export const RESET_PROXY_TO_UPDATE = 'PROFILE.RESET_PROXY_TO_UPDATE';
export const UPDATE_PROXY = 'PROFILE.UPDATE_PROXY';

export const GET_VISITPAY_ENROLLED_INFO = 'PROFILE.GET_VISITPAY_ENROLLED';
export const GET_VISITPAY_ENROLLED_INFO_SUCCESS = 'PROFILE.GET_VISITPAY_ENROLLED_SUCCESS';
export const GET_VISITPAY_ENROLLED_INFO_FAIL = 'PROFILE.GET_VISITPAY_ENROLLED_FAIL';

export const getProfileForInsurance = (consumerId?: string, useRequestState = true): AnyAction => {
  const headers: { consumerId?: string } = {};

  if (consumerId) {
    headers.consumerId = consumerId;
  }

  const isExpansion = Config.CONSUMERS_EXPANSION_ENABLED === 'enabled';
  const url = isExpansion
    ? getDispatcherUrl(
        DispatcherCategoryService.CONSUMER,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.CONSUMERS,
        DispatcherEndpoint.ID_INSURANCE
      )
    : '/consumer/consumerProfile2/insurance';

  return {
    type: consumerId ? GET_PROFILE_FOR_CONSUMER_INSURANCE : GET_PROFILE_FOR_INSURANCE,
    payload: {
      client: isExpansion ? 'dispatcher' : 'profile',
      useRequestState,
      requestStateSelector: profileInsuranceRequestStateSelector,
      request: {
        url,
        headers: {
          ...headers,
          [DFDHeaders.mhpAdapter]: 'mhp'
        }
      }
    }
  };
};

interface GetProfileHeaders extends Partial<DFDRequestHeaders> {
  consumerId?: string;
}

export const getProfile = (
  consumerId?: string,
  refresh?: boolean,
  useRequestState = false
): Thunk => (dispatch: RootDispatch, getState: () => RootState) => {
  const state = getState();

  const token = state.authentication?.dfdAuth?.accessToken;
  if (!token) {
    // Not authenticated yet, so reset the profile
    return dispatch({
      type: RESET_PROFILE
    });
  }
  const headers: GetProfileHeaders = {};
  const url = getDispatcherUrl(
    DispatcherCategoryService.CONSUMER,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.CONSUMERS,
    DispatcherEndpoint.ID
  );

  if (refresh) {
    headers[DFDHeaders.mhpCacheControl] = 'no-cache';
  }

  if (consumerId) {
    // Special handling for account switching.
    // Overrides default functionality for these headers so that they
    // contain target proxy values instead of those for the current proxy.
    headers[DFDHeaders.consumerId] = consumerId;

    const personId = getGrantPersonIdByConsumerId(consumerId, state);
    if (personId) {
      headers[DFDHeaders.patientId] = personId;
    }
  }
  const profileAction = {
    type: consumerId ? GET_PROFILE_FOR_CONSUMER : GET_PROFILE,
    payload: {
      client: 'dispatcher',
      useRequestState,
      requestStateSelector: profileRequestStateSelector,
      request: {
        url,
        headers: {
          ...headers,
          [DFDHeaders.mhpAdapter]: 'mhp'
        }
      }
    }
  };

  // Run this action as a fire and forget action
  dispatch(getProfileForInsurance(consumerId));

  return dispatch(profileAction);
};

export const getVisitpayEnrolledInfo = () => {
  return {
    type: GET_VISITPAY_ENROLLED_INFO,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'get',
        url: getDispatcherUrl(
          DispatcherCategoryService.BILLING,
          DispatcherAuthPrefix.AUTHENTICATED,
          DispatcherResource.VISIT_PAY
        ),
        headers: {
          [DFDHeaders.mhpAdapter]: AdapterSource.UTAH
        }
      }
    }
  };
};

export const SET_ACTIVE_INSURANCE = 'PROFILE.SET_ACTIVE_INSURANCE';
export const setProfileActiveInsurance = (insurance: Insurance) => ({
  type: SET_ACTIVE_INSURANCE,
  payload: insurance || null
});

export const UPDATE_CONSUMER_PROFILE = 'PROFILE.UPDATE_CONSUMER_PROFILE';
export const UPDATE_CONSUMER_PROFILE_SUCCESS = 'PROFILE.UPDATE_CONSUMER_PROFILE_SUCCESS';
export const UPDATE_CONSUMER_PROFILE_FAIL = 'PROFILE.UPDATE_CONSUMER_PROFILE_FAIL';

export type UpdatedConsumerOrUpdateFunction = ((consumer: Consumer) => Consumer) | Consumer;

const UPDATE_CONSUMER_PROFILE_TIMEOUT = 120 * 1000;

export const updateConsumerProfile = (
  updateConsumer: UpdatedConsumerOrUpdateFunction,
  updatedFields: string[]
): Thunk => (dispatch, getState) => {
  const updatedConsumer =
    typeof updateConsumer === 'function'
      ? updateConsumer(profileConsumerSelector(getState()))
      : updateConsumer;

  if (updatedConsumer.systemIds) {
    delete updatedConsumer.systemIds;
  }

  const isExpansion = Config.CONSUMERS_EXPANSION_ENABLED === 'enabled';
  let url = '';
  if (isExpansion) {
    url = getDispatcherUrl(
      DispatcherCategoryService.CONSUMER,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.CONSUMERS,
      DispatcherEndpoint.ID
    );

    url = `${url}/profile`;
  } else {
    url = '/consumer/consumerProfile';
  }

  return dispatch({
    type: UPDATE_CONSUMER_PROFILE,
    payload: {
      client: isExpansion ? 'dispatcher' : 'profile',
      request: {
        timeout: UPDATE_CONSUMER_PROFILE_TIMEOUT,
        method: 'put',
        url,
        data: {
          updatedFields,
          consumer: updatedConsumer
        },
        headers: { [DFDHeaders.mhpAdapter]: 'mhp' }
      }
    }
  });
};

export const updateConsumerEmail = (consumer: Consumer, email: string) => ({
  ...consumer,
  emails: [
    {
      primary: true,
      value: email,
      type: 'HOME',
      verified: false
    }
  ]
});

export const updateConsumerPhone = (consumer: Consumer, phone: string) => ({
  ...consumer,
  phones: [
    {
      primary: true,
      type: 'MOBILE',
      value: phone,
      verified: false
    }
  ]
});

export const UPDATE_PROFILE_MFA_ENABLED_FLAG = 'PROFILE.UPDATE_PROFILE_MFA_ENABLED_FLAG';
export const UPDATE_PROFILE_MFA_ENABLED_FLAG_SUCCESS =
  'PROFILE.UPDATE_PROFILE_MFA_ENABLED_FLAG_SUCCESS';
export const UPDATE_PROFILE_MFA_ENABLED_FLAG_FAIL = 'PROFILE.UPDATE_PROFILE_MFA_ENABLED_FLAG_FAIL';

export const updateConsumerMfaEnabledFlag = (mfaEnabled: boolean): Thunk => dispatch => {
  let url = getDispatcherUrl(
    DispatcherCategoryService.CONSUMER,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.CONSUMERS,
    DispatcherEndpoint.ID
  );
  url = `${url}/profile/mfa`;
  return dispatch({
    type: UPDATE_PROFILE_MFA_ENABLED_FLAG,
    payload: {
      client: 'dispatcher',
      request: {
        timeout: UPDATE_CONSUMER_PROFILE_TIMEOUT,
        method: 'put',
        url,
        data: {
          mfaEnabled
        },
        headers: { [DFDHeaders.mhpAdapter]: 'mhp' }
      }
    }
  });
};

export const GET_CONSUMER_MFA_STATUS = 'PROFILE.GET_CONSUMER_MFA_STATUS';
export const GET_CONSUMER_MFA_STATUS_SUCCESS = 'PROFILE.GET_CONSUMER_MFA_STATUS_SUCCESS';
export const GET_CONSUMER_MFA_STATUS_FAIL = 'PROFILE.GET_CONSUMER_MFA_STATUS_FAIL';

export const getConsumerMfaStatus = (): Thunk => dispatch => {
  let url = getDispatcherUrl(
    DispatcherCategoryService.CONSUMER,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.CONSUMERS,
    DispatcherEndpoint.ID
  );
  url = `${url}/profile/mfa`;
  return dispatch({
    type: GET_CONSUMER_MFA_STATUS,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'get',
        url,
        headers: { [DFDHeaders.mhpAdapter]: 'mhp' }
      }
    }
  });
};

// DEPRECATED
export const SEND_PIN_OTP = 'PROFILE.SEND_PIN_OTP';
export const SEND_PIN_OTP_SUCCESS = 'PROFILE.SEND_PIN_OTP_SUCCESS';
export const SEND_PIN_OTP_FAIL = 'PROFILE.SEND_PIN_OTP_FAIL';
export const sendPinOTP = (props: SendPinProps): Thunk => (dispatch, getState) => {
  const { type, phone, email } = props;
  const allowedTypes = ['email', 'sms'];

  if (
    allowedTypes.indexOf(type) < 0 ||
    (type === 'email' && !email) ||
    (type === 'sms' && !phone)
  ) {
    return false;
  }

  const consumer = profileConsumerSelector(getState());
  const pendingConsumer =
    type === 'email' ? updateConsumerEmail(consumer, email) : updateConsumerPhone(consumer, phone);

  return dispatch({
    type: SEND_PIN_OTP,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/iddataweb/sendotpcode',
        data: {
          consumer: {
            registrationStatus: 'Contact',
            ...pendingConsumer
          },
          consumerVerification: {
            sendOTP: true,
            verifyUser: false,
            verificationStatus: 'SendOTP',
            otpType: type
          }
        }
      }
    }
  });
};
// DEPRECATED
export const SEND_PIN = 'PROFILE.SEND_PIN';
export const SEND_PIN_SUCCESS = 'PROFILE.SEND_PIN_SUCCESS';
export const SEND_PIN_FAIL = 'PROFILE.SEND_PIN_FAIL';
export const sendPin = (props: SendPinProps): Thunk => (dispatch, getState) => {
  const { type, phone, email } = props;
  const allowedTypes = ['email', 'sms'];

  if (
    allowedTypes.indexOf(type) < 0 ||
    (type === 'email' && !email) ||
    (type === 'sms' && !phone)
  ) {
    return false;
  }

  const consumer = profileConsumerSelector(getState());
  const pendingConsumer =
    type === 'email' ? updateConsumerEmail(consumer, email) : updateConsumerPhone(consumer, phone);

  return dispatch({
    type: SEND_PIN,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/iddataweb/sendotpcode',
        data: {
          consumer: {
            registrationStatus: 'Contact',
            ...pendingConsumer
          },
          consumerVerification: {
            sendOTP: true,
            verifyUser: false,
            verificationStatus: 'SendOTP',
            otpType: type
          }
        }
      }
    }
  });
};
// DEPRECATED
export const VALIDATE_PIN_OTP = 'PROFILE.VALIDATE_PIN_OTP';
export const VALIDATE_PIN_OTP_SUCCESS = 'PROFILE.VALIDATE_PIN_OTP_SUCCESS';
export const VALIDATE_PIN_OTP_FAIL = 'PROFILE.VALIDATE_PIN_OTP_FAIL';
export const validatePinOTP = (props: ValidatePinProps): Thunk => (dispatch, getState) => {
  const { email, phone, pin, type } = props;
  const allowedTypes = ['email', 'sms'];

  if (
    allowedTypes.indexOf(type) < 0 ||
    (type === 'email' && !email) ||
    (type === 'sms' && !phone)
  ) {
    return false;
  }

  const consumer = profileConsumerSelector(getState());
  const pendingConsumer =
    type === 'email' ? updateConsumerEmail(consumer, email) : updateConsumerPhone(consumer, phone);

  return dispatch({
    type: VALIDATE_PIN_OTP,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/iddataweb/validateotpcode',
        data: {
          consumer: {
            registrationStatus: 'Contact',
            ...pendingConsumer
          },
          consumerVerification: {
            sendOTP: false,
            verifyUser: false,
            verificationStatus: 'ValidateOTP',
            otpType: type,
            validateOTP: true,
            pin
          }
        }
      }
    }
  });
};
// DEPRECATED
export const VALIDATE_PIN = 'PROFILE.VALIDATE_PIN';
export const VALIDATE_PIN_SUCCESS = 'PROFILE.VALIDATE_PIN_SUCCESS';
export const VALIDATE_PIN_FAIL = 'PROFILE.VALIDATE_PIN_FAIL';
export const validatePin = (props: ValidatePinProps): Thunk => (dispatch, getState) => {
  const { email, phone, pin, type } = props;
  const allowedTypes = ['email', 'sms'];

  if (
    allowedTypes.indexOf(type) < 0 ||
    (type === 'email' && !email) ||
    (type === 'sms' && !phone)
  ) {
    return false;
  }

  const consumer = profileConsumerSelector(getState());
  const pendingConsumer =
    type === 'email' ? updateConsumerEmail(consumer, email) : updateConsumerPhone(consumer, phone);

  return dispatch({
    type: VALIDATE_PIN,
    payload: {
      client: 'profile',
      request: {
        method: 'post',
        url: '/iddataweb/validateotpcode',
        data: {
          consumer: {
            registrationStatus: 'Contact',
            ...pendingConsumer
          },
          consumerVerification: {
            sendOTP: false,
            verifyUser: false,
            verificationStatus: 'ValidateOTP',
            otpType: type,
            validateOTP: true,
            pin
          }
        }
      }
    }
  });
};

export const VALIDATE_CONSUMER_ADDRESS = 'PROFILE.VALIDATE_CONSUMER_ADDRESS';
export const VALIDATE_CONSUMER_ADDRESS_SUCCESS = 'PROFILE.VALIDATE_CONSUMER_ADDRESS_SUCCESS';
export const VALIDATE_CONSUMER_ADDRESS_FAIL = 'PROFILE.VALIDATE_CONSUMER_ADDRESS_FAIL';
export const validateConsumerAddress = (update: Consumer) => ({
  type: VALIDATE_CONSUMER_ADDRESS,
  payload: {
    client: 'profile',
    request: {
      method: 'post',
      url: '/iddataweb/verify',
      data: {
        consumer: {
          registrationStatus: 'Contact',
          ...update
        },
        consumerVerification: {
          sendOTP: false,
          verifyUser: true,
          verificationStatus: 'VerifyUser',
          validateOTP: false
        }
      }
    }
  }
});

export const UPDATE_CONSUMER_CONTACT_EMAIL = 'PROFILE.UPDATE_CONSUMER_CONTACT_EMAIL';
export const updateConsumerContactEmail = (email: string) => {
  return {
    type: UPDATE_CONSUMER_CONTACT_EMAIL,
    payload: {
      data: {
        primary: true,
        value: email,
        type: 'HOME',
        verified: false
      }
    }
  };
};

export const UPDATE_CONSUMER_CONTACT_PHONE = 'PROFILE.UPDATE_CONSUMER_CONTACT_PHONE';
export const updateConsumerContactPhone = (phoneNumber: string) => ({
  type: UPDATE_CONSUMER_CONTACT_PHONE,
  payload: {
    data: {
      primary: true,
      value: phoneNumber,
      type: 'MOBILE',
      verified: false
    }
  }
});

export const RESET_VALIDATION = 'PROFILE.RESET_VALIDATION';
export const resetValidationState = (key?: string) => ({
  type: RESET_VALIDATION,
  payload: { key }
});

// DEPRECATED
export const savePrimaryPhone = (value: string, type: 'MOBILE' | 'HOME' | 'WORK') => (
  dispatch: RootDispatch,
  getState: () => RootState
) => {
  // clean the new data
  if (!value) {
    return Promise.reject(new Error('No Phone Number to update'));
  }

  const phoneNumber = cleanPhone(value);
  // get existing phone numbers
  const state = getState();
  const consumer = profileConsumerSelector(state);
  consumer.phones = consumer.phones || []; // Trust but verify
  const existing = consumer.phones.find(phone => phone.value === phoneNumber);

  if (existing) {
    // Update it to primary if already existing
    existing.primary = true;
  } else {
    // Save new Phone
    consumer.phones.push({
      primary: true,
      value: phoneNumber,
      verified: false,
      type
    });
  }

  return dispatch(updateConsumerProfile(consumer, ['Phone']));
};

export const acceptEVisitTermsOfUse = (): Thunk => (
  dispatch: Function,
  getState: () => RootState
) => {
  const state = getState();
  const consumer = profileConsumerSelector(state);
  consumer.externalTermsAcceptance = consumer.externalTermsAcceptance || [];

  const filteredTerms = consumer.externalTermsAcceptance.filter(
    ({ type }) => !type.includes('E_VISIT')
  );

  filteredTerms.push({
    type: 'E_VISIT',
    version: termsConditions.eVisitTermsOfUse.version,
    date: new Date().toISOString()
  });

  consumer.externalTermsAcceptance = filteredTerms;
  return dispatch(updateConsumerProfile(consumer, ['ExternalTermsAcceptance']));
};

export const addRequestedProxy = (proxies: RequestedProxy[]) => ({
  type: ADD_REQUESTED_PROXIES,
  payload: {
    data: proxies
  }
});

export const clearRequestedProxies = () => ({
  type: CLEAR_REQUESTED_PROXIES
});

export const deleteRequestedProxy = (id: string) => (
  dispatch: RootDispatch,
  getState: () => RootState
) => {
  const proxies = profileRequestedProxies(getState());
  const proxiesUpdated = proxies.filter(proxy => proxy.id !== id);

  const proxyAction = {
    type: DELETE_REQUESTED_PROXY,
    payload: {
      data: proxiesUpdated
    }
  };

  dispatch(proxyAction);
};

export const setProxyToUpdate = (proxy: RequestedProxy) => ({
  type: SET_PROXY_TO_UPDATE,
  payload: {
    data: proxy
  }
});

export const resetProxyToUpdate = () => ({
  type: RESET_PROXY_TO_UPDATE
});

export const updateProxy = (proxyUpdated: RequestedProxy) => (
  dispatch: RootDispatch,
  getState: () => RootState
) => {
  const proxies = profileRequestedProxies(getState());
  const proxiesUpdated = proxies.map(proxy =>
    proxy.id === proxyUpdated.id ? proxyUpdated : proxy
  );

  const proxyAction = {
    type: UPDATE_PROXY,
    payload: {
      data: proxiesUpdated
    }
  };

  dispatch(proxyAction);
};

export const SEND_IDW_PIN = 'PROFILE.SEND_IDW_PIN';
export const SEND_IDW_PIN_FAIL = 'PROFILE.SEND_IDW_PIN_FAIL';
export const SEND_IDW_PIN_RESET = 'PROFILE.SEND_IDW_PIN_RESET';
export const SEND_IDW_PIN_SUCCESS = 'PROFILE.SEND_IDW_PIN_SUCCESS';
export function sendIDWPin(sendType: 'phone' | 'email', value: string) {
  const url = getDispatcherUrl(
    DispatcherCategoryService.CONSUMER,
    DispatcherAuthPrefix.PUBLIC,
    DispatcherResource.IDDATAWEB,
    DispatcherEndpoint.SEND_CODE
  );
  return {
    type: SEND_IDW_PIN,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'post',
        url,
        headers: { [DFDHeaders.mhpAdapter]: 'mhp' },
        data: {
          sendType,
          value: value.toUpperCase()
        }
      }
    }
  };
}

export const resetOtpState = () => ({
  type: SEND_IDW_PIN_RESET
});

export const SEND_IDW_VERIFICATION = 'PROFILE.SEND_IDW_VERIFICATION';
export const SEND_IDW_VERIFICATION_FAIL = 'PROFILE.SEND_IDW_VERIFICATION_FAIL';
export const SEND_IDW_VERIFICATION_SUCCESS = 'PROFILE.SEND_IDW_VERIFICATION_SUCCESS';
export function sendIDWVerification(sendType: 'phone' | 'email', value: string, code: string) {
  const url = getDispatcherUrl(
    DispatcherCategoryService.CONSUMER,
    DispatcherAuthPrefix.PUBLIC,
    DispatcherResource.IDDATAWEB,
    DispatcherEndpoint.VALIDATE
  );
  return {
    type: SEND_IDW_VERIFICATION,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'post',
        url,
        headers: { [DFDHeaders.mhpAdapter]: 'mhp' },
        data: {
          sendType,
          value: value.toUpperCase(),
          code
        }
      }
    }
  };
}

export const UPDATE_EMAIL = 'PROFILE.UPDATE_EMAIL';
export const UPDATE_EMAIL_FAIL = 'PROFILE.UPDATE_EMAIL_FAIL';
export const UPDATE_EMAIL_SUCCESS = 'PROFILE.UPDATE_EMAIL_SUCCESS';
export function updateEmail(data: UpdateEmailProps) {
  const url = getDispatcherUrl(
    DispatcherCategoryService.CONSUMER,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.CONSUMERS,
    `${DispatcherEndpoint.ID}/profile/email`
  );
  return {
    type: UPDATE_EMAIL,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'put',
        url,
        headers: { [DFDHeaders.mhpAdapter]: 'mhp' },
        data: { ...data, type: 'HOMEEMAIL', email: data.email.toUpperCase() }
      }
    }
  };
}

export const UPDATE_PHONE = 'PROFILE.UPDATE_PHONE';
export const UPDATE_PHONE_FAIL = 'PROFILE.UPDATE_PHONE_FAIL';
export const UPDATE_PHONE_SUCCESS = 'PROFILE.UPDATE_PHONE_SUCCESS';
export function updatePhone(data: {
  phone: string;
  verifyCode: string;
  profileVerifyValue: string;
  profileVerifyCode: string;
  profileVerifyType: 'phone' | 'email';
}) {
  const url = getDispatcherUrl(
    DispatcherCategoryService.CONSUMER,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.CONSUMERS,
    `${DispatcherEndpoint.ID}/profile/phone`
  );
  // TODO updateConsumersByIdProfilePhone
  return {
    type: UPDATE_PHONE,
    payload: {
      client: 'dispatcher',
      request: {
        method: 'put',
        url,
        headers: { [DFDHeaders.mhpAdapter]: 'mhp' },
        data: { ...data, type: 'CELLPHONE' }
      }
    }
  };
}
